import * as THREE from 'three';
import { PlayerRole } from '@lucky-vr/common';
import type { Renderer } from '../core/Renderer.js';
import type { InputManager } from '../core/InputManager.js';

/**
 * Owns the WebXR session and seats the player in the vehicle: the XR camera
 * and both controllers are parented to the cockpit rig, so headset/controller
 * poses are relative to the (moving) car — the world drives past you.
 *
 * Controller grips carry visible hand props (laser pistols / steering glove)
 * so the player sees their hands in-headset.
 */
export class XRSessionManager {
  private renderer: Renderer;
  private input: InputManager;
  private rig: THREE.Object3D;
  private session: XRSession | null = null;
  private role: PlayerRole | null = null;

  onSessionStart: (() => void) | null = null;
  onSessionEnd: (() => void) | null = null;

  constructor(renderer: Renderer, input: InputManager, rig: THREE.Object3D) {
    this.renderer = renderer;
    this.input = input;
    this.rig = rig;
  }

  async checkSupport(): Promise<boolean> {
    return this.renderer.initXR();
  }

  /** Called whenever the server (re)assigns our role — updates hand props. */
  setRole(role: PlayerRole): void {
    this.role = role;
    if (this.isInVR) this.attachHandProps();
  }

  async enterVR(): Promise<boolean> {
    this.session = await this.renderer.enterVR();
    if (!this.session) return false;

    const camera = this.renderer.camera;
    const xr = this.renderer.renderer.xr;

    // Seat the player: XR poses ('local' space, origin at the head's starting
    // pose) apply on top of the rig transform, which follows the car.
    this.rig.add(camera);
    camera.position.set(0, 0, 0);
    camera.quaternion.identity();

    this.input.setupXRControllers();
    for (let i = 0; i < 2; i++) {
      this.rig.add(xr.getController(i));
      this.rig.add(xr.getControllerGrip(i));
    }
    this.attachHandProps();

    this.session.addEventListener('end', () => {
      this.session = null;
      for (let i = 0; i < 2; i++) {
        xr.getController(i).removeFromParent();
        xr.getControllerGrip(i).removeFromParent();
      }
      this.input.endXRSession();
      camera.removeFromParent(); // desktop code drives the camera in world space
      this.onSessionEnd?.();
    });

    this.onSessionStart?.();
    return true;
  }

  exitVR(): void {
    this.session?.end();
  }

  get isInVR(): boolean {
    return this.session !== null;
  }

  /** World position of a hand (VR only) — used as the muzzle origin. */
  getHandWorldPosition(hand: 'left' | 'right'): THREE.Vector3 | null {
    if (!this.isInVR) return null;
    const controller = this.controllerForHand(hand);
    return controller ? controller.getWorldPosition(new THREE.Vector3()) : null;
  }

  // ----------------------------------------------------------------
  // Hand props — bound by REAL handedness (from the XRInputSource),
  // not by controller index, which runtimes don't guarantee.
  // ----------------------------------------------------------------

  private controllerForHand(hand: 'left' | 'right'): THREE.Object3D | null {
    const xr = this.renderer.renderer.xr;
    for (let i = 0; i < 2; i++) {
      const controller = xr.getController(i);
      if (controller.userData.hand === hand) return controller;
    }
    return null;
  }

  private attachHandProps(): void {
    const xr = this.renderer.renderer.xr;
    for (let i = 0; i < 2; i++) {
      // TARGET-RAY space (where you point) — grip space is tilted ~35°,
      // which made bullets appear to leave the barrel at an angle
      const controller = xr.getController(i);
      if (!controller.userData.propBound) {
        controller.userData.propBound = true;
        // handedness arrives with the input source; refresh the prop then
        controller.addEventListener('connected', (event: any) => {
          controller.userData.hand = event.data?.handedness === 'left' ? 'left' : 'right';
          this.refreshProp(controller);
        });
        controller.addEventListener('disconnected', () => this.clearProp(controller));
      }
      this.refreshProp(controller); // covers re-entry when 'connected' already fired
    }
  }

  private clearProp(holder: THREE.Object3D): void {
    const prop = holder.userData.prop as THREE.Group | undefined;
    if (prop) {
      prop.removeFromParent();
      disposeGroup(prop);
      holder.userData.prop = undefined;
    }
  }

  private refreshProp(holder: THREE.Object3D): void {
    this.clearProp(holder);
    const hand = holder.userData.hand as 'left' | 'right' | undefined;
    if (!hand) return; // not connected yet

    let prop: THREE.Group;
    if (this.role === PlayerRole.Driver) {
      // left hand steers, right hand shoots
      prop = hand === 'left' ? buildGlove(0xff2d95) : buildLaserPistol(0x2ee6d6);
    } else {
      // gunner (or unknown yet): dual-wield
      prop = buildLaserPistol(hand === 'left' ? 0xff2d95 : 0x2ee6d6);
    }
    holder.add(prop);
    holder.userData.prop = prop;
  }
}

/** Chunky 80s laser pistol, gripped along the controller handle. */
function buildLaserPistol(accent: number): THREE.Group {
  const gun = new THREE.Group();
  const dark = new THREE.MeshLambertMaterial({ color: 0x23262e });
  const glow = new THREE.MeshBasicMaterial({ color: accent });

  // receiver
  const receiver = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.07, 0.22), dark);
  receiver.position.set(0, 0.02, -0.06);
  gun.add(receiver);

  // barrel with glowing emitter tip
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.016, 0.016, 0.14, 8), dark);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.035, -0.22);
  gun.add(barrel);

  const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.02, 8), glow);
  tip.rotation.x = Math.PI / 2;
  tip.position.set(0, 0.035, -0.29);
  gun.add(tip);

  // neon side stripes
  for (const side of [-1, 1]) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.02, 0.16), glow);
    stripe.position.set(side * 0.028, 0.03, -0.06);
    gun.add(stripe);
  }

  // grip (the controller handle sits inside it)
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.042, 0.11, 0.05), dark);
  handle.position.set(0, -0.05, 0.02);
  handle.rotation.x = 0.35;
  gun.add(handle);

  // barrel points exactly along the target ray (-Z): what you see is
  // where the bullets go
  return gun;
}

/** Driver's left hand: a simple glove with a neon wristband (it steers, not shoots). */
function buildGlove(accent: number): THREE.Group {
  const glove = new THREE.Group();
  const dark = new THREE.MeshLambertMaterial({ color: 0x2a2d36 });

  const fist = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.09, 0.11), dark);
  glove.add(fist);

  const band = new THREE.Mesh(
    new THREE.BoxGeometry(0.075, 0.03, 0.115),
    new THREE.MeshBasicMaterial({ color: accent })
  );
  band.position.set(0, -0.05, 0.03);
  glove.add(band);

  return glove;
}

function disposeGroup(group: THREE.Group): void {
  group.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => m.dispose());
    }
  });
}
